import { useEffect, useRef, useState } from 'react';

/**
 * Custom hook for scroll-triggered animations using Intersection Observer
 * @param {Object} options - Configuration options
 * @param {number} options.threshold - Visibility threshold (0-1)
 * @param {string} options.rootMargin - Margin around root
 * @param {boolean} options.triggerOnce - Only trigger animation once
 * @returns {Array} [ref, isVisible]
 */
export const useScrollAnimation = (options = {}) => {
  const { 
    threshold = 0.1, 
    rootMargin = '0px 0px -50px 0px',
    triggerOnce = true 
  } = options;
  
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (triggerOnce) {
            observer.unobserve(element);
          }
        } else if (!triggerOnce) {
          setIsVisible(false);
        }
      },
      { threshold, rootMargin }
    );

    observer.observe(element);

    return () => {
      if (element) observer.unobserve(element);
    };
  }, [threshold, rootMargin, triggerOnce]);

  return [ref, isVisible];
};

/**
 * Hook for staggered children animations
 * @param {number} childCount - Number of children to animate
 * @param {number} staggerDelay - Delay between each child (ms)
 * @returns {Array} [ref, isVisible, getChildDelay]
 */
export const useStaggerAnimation = (childCount, staggerDelay = 100) => {
  const [ref, isVisible] = useScrollAnimation();
  
  const getChildDelay = (index) => ({
    transitionDelay: `${index * staggerDelay}ms`
  });

  return [ref, isVisible, getChildDelay];
};

export default useScrollAnimation;
